/**
 * Phase 7 — player roster briefs page.
 *
 * Two routes share this file:
 *   /tactics/:sessionId/briefs              → index of all 11 home players
 *   /tactics/:sessionId/briefs/:playerKey   → single-player detail page
 *
 * Reads the active TacticsLayout from localStorage via
 * `loadTacticsLayout` keyed on `tactics-3d-${sessionId}` — the same
 * persistKey the 2D + 3D boards use. The brief is rendered straight
 * from `layout.playerBriefs[playerKey]`; sections without bullets are
 * hidden so a print-out doesn't show empty headings.
 *
 * Print-friendly: a `@media print` block in TacticBriefs.css hides the
 * chrome (header, back link, etc.) and lays each detail page out as
 * one A4 page with a small reference pitch top-right.
 *
 * Auth-aware filtering: deferred until web2 has player-role auth. For
 * now this is coach-only and renders every brief.
 */

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PlayerBrief } from '@4cm/core/tactics';
import { loadTacticsLayout } from '../lib/tactics3dStore';
import './TacticBriefs.css';

// TODO(auth): scope to authed player's playerKey when login lands.

interface SectionDef {
  key: keyof Pick<PlayerBrief, 'inPossession' | 'outOfPossession' | 'transitions' | 'setPieces'>;
  label: string;
}

const SECTIONS: SectionDef[] = [
  { key: 'inPossession', label: 'In possession' },
  { key: 'outOfPossession', label: 'Out of possession' },
  { key: 'transitions', label: 'Transitions' },
  { key: 'setPieces', label: 'Set pieces' },
];

export function TacticBriefsIndex() {
  const { sessionId } = useParams();
  const persistKey = sessionId ? `tactics-3d-${sessionId}` : 'tactics-3d-default';
  const layout = useMemo(() => loadTacticsLayout(persistKey), [persistKey]);

  if (!layout) {
    return (
      <main className="tactic-briefs">
        <header className="tactic-briefs__header">
          <Link to={sessionId ? `/sessions/${sessionId}` : '/'} className="tactic-briefs__back">
            ← Back
          </Link>
          <h1 className="tactic-briefs__title">Player briefs</h1>
        </header>
        <p className="tactic-briefs__empty">
          No tactics layout found for this session. Open the 2D or 3D board, draw a tactic, and generate
          briefs first.
        </p>
      </main>
    );
  }

  const briefs = (layout.playerBriefs ?? {}) as Record<string, PlayerBrief>;
  const briefList = layout.homeSpots.map((spot, idx) => ({
    playerKey: `home-${idx}`,
    slot: spot.role,
    brief: briefs[`home-${idx}`] ?? null,
  }));
  const hasAnyBriefs = briefList.some((b) => b.brief !== null);

  return (
    <main className="tactic-briefs">
      <header className="tactic-briefs__header">
        <Link to={sessionId ? `/sessions/${sessionId}/2d` : '/'} className="tactic-briefs__back">
          ← Back to board
        </Link>
        <h1 className="tactic-briefs__title">Player briefs</h1>
        <div className="tactic-briefs__actions">
          <button
            type="button"
            className="tactic-briefs__print"
            onClick={() => window.print()}
            aria-label="Print all briefs"
          >
            Print
          </button>
        </div>
      </header>

      {!hasAnyBriefs && (
        <p className="tactic-briefs__empty">
          No briefs generated yet for this tactic. Open the board, tap <strong>Briefs → Generate</strong>,
          then come back to print or share.
        </p>
      )}

      {hasAnyBriefs && (
        <ul className="tactic-briefs__list">
          {briefList.map(({ playerKey, slot, brief }) => (
            <li key={playerKey} className="tactic-briefs__list-item">
              <Link
                to={`/tactics/${sessionId}/briefs/${playerKey}`}
                className="tactic-briefs__list-link"
              >
                <span className="tactic-briefs__list-slot">{slot}</span>
                <span className="tactic-briefs__list-key">{playerKey}</span>
                {brief?.namedRole && (
                  <span className="tactic-briefs__list-role">{brief.namedRole}</span>
                )}
                {brief && (
                  <span className="tactic-briefs__list-preview">
                    {brief.inPossession[0] ?? brief.outOfPossession[0] ?? '—'}
                  </span>
                )}
                {!brief && (
                  <span className="tactic-briefs__list-empty">No brief generated</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

export function TacticBriefDetail() {
  const { sessionId, playerKey } = useParams();
  const persistKey = sessionId ? `tactics-3d-${sessionId}` : 'tactics-3d-default';
  const layout = useMemo(() => loadTacticsLayout(persistKey), [persistKey]);

  const brief = useMemo<PlayerBrief | null>(() => {
    if (!layout || !playerKey) return null;
    const map = (layout.playerBriefs ?? {}) as Record<string, PlayerBrief>;
    return map[playerKey] ?? null;
  }, [layout, playerKey]);

  if (!layout) {
    return (
      <main className="tactic-briefs tactic-briefs--detail">
        <BackHeader sessionId={sessionId} />
        <p className="tactic-briefs__empty">No tactics layout found for this session.</p>
      </main>
    );
  }

  if (!brief) {
    return (
      <main className="tactic-briefs tactic-briefs--detail">
        <BackHeader sessionId={sessionId} />
        <p className="tactic-briefs__empty">
          No brief generated for <strong>{playerKey}</strong>. Open the board, tap{' '}
          <strong>Briefs → Generate</strong>, and try again.
        </p>
      </main>
    );
  }

  return (
    <main
      className="tactic-briefs tactic-briefs--detail"
      data-testid={`tactic-brief-page-${brief.playerKey}`}
    >
      <BackHeader sessionId={sessionId} />
      <section className="tactic-briefs__detail">
        <header className="tactic-briefs__detail-header">
          <div className="tactic-briefs__detail-slot">{brief.slot}</div>
          <div className="tactic-briefs__detail-meta">
            <span className="tactic-briefs__detail-key">{brief.playerKey}</span>
            {brief.namedRole && (
              <span className="tactic-briefs__detail-role">{brief.namedRole}</span>
            )}
            <span className="tactic-briefs__detail-source" data-source={brief.source}>
              {brief.source}
            </span>
          </div>
        </header>

        {SECTIONS.map((section) => {
          const lines = brief[section.key];
          if (!lines || lines.length === 0) return null;
          return (
            <section key={section.key} className="tactic-briefs__detail-section">
              <h2 className="tactic-briefs__detail-section-label">{section.label}</h2>
              <ul className="tactic-briefs__detail-bullets">
                {lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </section>
          );
        })}

        <footer className="tactic-briefs__detail-footer">
          <button
            type="button"
            className="tactic-briefs__print"
            onClick={() => window.print()}
            aria-label="Print this brief"
          >
            Print
          </button>
        </footer>
      </section>
    </main>
  );
}

function BackHeader({ sessionId }: { sessionId?: string }) {
  return (
    <header className="tactic-briefs__header">
      <Link
        to={sessionId ? `/tactics/${sessionId}/briefs` : '/'}
        className="tactic-briefs__back"
      >
        ← All briefs
      </Link>
    </header>
  );
}
